import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import moment from 'moment'
import type { CarHireSearchSortTypes } from './CarHireSearchPage/CarHireSearchPageSort'
import { sortBy } from 'lib/array/arrayUtils'
import { getAge } from 'lib/datetime/dateUtils'

export function getCarHireListKey(filters: App.CarHireOfferListFilters) {
  const tokens = [
    filters.driversAgeCategory,
    filters.dropOffDate,
    filters.dropOffTime,
    filters.pickUpDate,
    filters.pickUpTime,
    filters.pickUpLocationId,
    filters.dropOffLocationId,
    filters.driversAge,
    filters.bundledOrderId,
    filters.pickUpLatitude,
    filters.pickUpLongitude,
    filters.dropOffLatitude,
    filters.dropOffLongitude,
  ]

  return tokens.join('-')
}

function mapParamsToSearchItem(id?: string | null, name?: string | null): App.SearchItem | undefined {
  if (id && name) {
    if (id.length === 3) {
      return {
        searchType: 'airport',
        airportCode: id,
        value: id,
        format: {
          mainText: name,
        },
      } as App.SearchAirport
    }

    return {
      searchType: 'destination',
      value: id,
      format: {
        mainText: name,
      },
      ...(id.includes('le') && {
        destinationType: 'city',
      }),
    }
  }
}

export function getCarHireSearchStateFromParams(queryParams: URLSearchParams): Partial<GlobalSearchState> {
  const ageCategory = queryParams.get('ageCategory')
  const pickUpItem = mapParamsToSearchItem(queryParams.get('pickUpId'), queryParams.get('pickUpName'))
  const dropOffItem = mapParamsToSearchItem(queryParams.get('dropOffId'), queryParams.get('dropOffName'))
  const pickUpDate = queryParams.get('pickUpDate')
  const dropoffDate = queryParams.get('dropOffDate')
  const age = queryParams.get('age')
  const isSameLocation = pickUpItem?.format.mainText === dropOffItem?.format.mainText
  const bundledOrderId = queryParams.get('bundledOrderId') ?? undefined
  const pickUpLatitude = queryParams.get('pickUpLatitude') ? parseFloat(queryParams.get('pickUpLatitude') as string) : undefined
  const pickUpLongitude = queryParams.get('pickUpLongitude') ? parseFloat(queryParams.get('pickUpLongitude') as string) : undefined
  const dropOffLatitude = queryParams.get('dropOffLatitude') ? parseFloat(queryParams.get('dropOffLatitude') as string) : undefined
  const dropOffLongitude = queryParams.get('dropOffLongitude') ? parseFloat(queryParams.get('dropOffLongitude') as string) : undefined

  return {
    searchItem: pickUpItem,
    secondarySearchItem: isSameLocation ? undefined : dropOffItem,
    checkinDate: pickUpDate ? moment(pickUpDate) : undefined,
    checkoutDate: dropoffDate ? moment(dropoffDate) : undefined,
    pickUpTime: queryParams.get('pickUpTime') ?? undefined,
    dropOffTime: queryParams.get('dropOffTime') ?? undefined,
    driversAgeCategory: ageCategory ? parseInt(ageCategory) as App.CarHireDriverAgeCategory : undefined,
    driversAge: age ? parseInt(age) : undefined,
    bundledOrderId,
    pickUpLatitude,
    pickUpLongitude,
    dropOffLatitude,
    dropOffLongitude,
  }
}

export function sortCarHireOffers(offers: Array<App.CarHireOffer>, sort?: CarHireSearchSortTypes) {
  if (!sort) {
    return offers
  }

  switch (sort) {
    case 'price.asc':
    case 'price.desc':
      return sortBy(offers, res => res.price, sort === 'price.asc' ? 'asc' : 'desc')
    default:
      return offers
  }
}

/**
 * Works out and returns the drivers age category based on the age provider
 * @param age The age to get the category for
 * @returns The category for the given age
 */
export function getCarHireAgeCategory(age: number): App.CarHireDriverAgeCategory {
  if (age <= 29) {
    return 1
  } else if (age >= 70) {
    return 3
  }
  return 2
}

export function isCarHireItemDobValid(dob: string, item: App.Checkout.CarHireItem): boolean {
  const age = getAge(dob, new Date(item.pickUpDate))
  const ageCategory = getCarHireAgeCategory(age)

  if (item.ageCategory === 2) {
    return ageCategory === item.ageCategory
  }

  return age === item.age
}

export const CAR_HIRE_LOCATION_PREFIX = 'car_hire_location:'

function isCarHireLocation(searchItem: App.SearchItem): boolean {
  return Boolean(searchItem.value?.startsWith(CAR_HIRE_LOCATION_PREFIX))
}

export function stripCarHireLocationPrefix(locationId: string | undefined): string | undefined {
  if (!locationId) return locationId
  return locationId.startsWith(CAR_HIRE_LOCATION_PREFIX)
    ? locationId.slice(CAR_HIRE_LOCATION_PREFIX.length)
    : locationId
}

export function getCarHireSearchType(searchItem: App.SearchItem): App.CarHireSearchTypes {
  if (searchItem.searchType === 'airport') {
    return 'airport'
  }

  if (isCarHireLocation(searchItem)) {
    return 'destination'
  }
  return 'geo'
}

export function getCarHireSearchId(searchItem?: App.SearchItem): string | undefined {
  if (!searchItem) return undefined

  if (searchItem.searchType === 'airport') {
    return searchItem.airportCode || searchItem.value
  }

  return searchItem.value
}
